import ClearIcon from '@mui/icons-material/Clear';
import { createTheme } from '@mui/material';

import { AlertIcon } from '../../components/Alert/icons/Alert';
import { CheckCircle } from '../../components/Alert/icons/CheckCircle';
import { InfoCircle } from '../../components/Alert/icons/InfoCircle';
import { Offer } from '../../components/Alert/icons/Offer';
import { sizeMap } from '../types/theme';
import { Colors } from './bolt-uikit-colors';
import { Typography } from './bolt-uikit-typography';
import { SkeletonTextSizeMap } from './bolt-uikit-variables';

interface Tokens {
  fontTokens: {
    fontFamily: string;
  };
  skeleton: {
    sizeMap: sizeMap;
  };
  typography: {
    [key: string]: {
      fontSize: string;
      lineHeight: string;
      fontWeight: number;
      color: string;
      letterSpacing?: string;
      uppercase?: boolean;
    };
  };
}

// Optional utility function for building typography variants
const makeFontSizeAndLineHeight = (
  fontSize: number,
  lineHeight: number,
  fontWeight: number,
  color: string,
  letterSpacing?: string,
  uppercase?: boolean,
) => ({
  fontFamily: Typography['font-family'],
  fontVariantNumeric: 'lining-nums tabular-nums',
  fontFeatureSettings: "'cv03' on, 'cv04' on",
  fontStyle: 'normal',
  fontSize: `${fontSize}px`,
  lineHeight: `${lineHeight}px`,
  fontWeight,
  color,
  letterSpacing: letterSpacing ? `${letterSpacing}px` : undefined,
  textTransform: uppercase ? 'uppercase' : undefined,
});

export const tokens: Tokens = {
  fontTokens: {
    fontFamily: Typography['font-family'],
  },
  skeleton: {
    sizeMap: SkeletonTextSizeMap,
  },
  typography: {
    'heading-l-accent': makeFontSizeAndLineHeight(
      Typography['Heading L']['font-size'],
      Typography['Heading L']['line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['500'],
    ),
    'heading-m-accent': makeFontSizeAndLineHeight(
      Typography['Heading M']['font-size'],
      Typography['Heading M']['line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      '-0.616',
    ),
    'heading-s-accent': makeFontSizeAndLineHeight(
      Typography['Heading S']['font-size'],
      Typography['Heading S']['line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      '-0.48',
    ),
    'heading-xs-accent': makeFontSizeAndLineHeight(
      Typography['Heading XS']['font-size'],
      Typography['Heading XS']['line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['700'],
    ),
    'body-l-regular': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['600'],
    ),
    'body-l-accent': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['600'],
    ),
    'body-l-compact-accent': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['600'],
    ),
    'body-l-compact-regular': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['600'],
    ),
    'body-m-regular': makeFontSizeAndLineHeight(
      Typography['Body M']['font-size'],
      Typography['Body M']['line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['500'],
    ),
    'body-m-accent': makeFontSizeAndLineHeight(
      Typography['Body M']['font-size'],
      Typography['Body M']['line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['500'],
    ),
    'body-m-compact-accent': makeFontSizeAndLineHeight(
      Typography['Body M']['font-size'],
      Typography['Body M']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['500'],
    ),
    'body-m-compact-regular': makeFontSizeAndLineHeight(
      Typography['Body M']['font-size'],
      Typography['Body M']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['600'],
    ),
    'body-s-regular': makeFontSizeAndLineHeight(
      Typography['Body S']['font-size'],
      Typography['Body S']['line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['400'],
    ),
    'body-s-accent': makeFontSizeAndLineHeight(
      Typography['Body S']['font-size'],
      Typography['Body S']['line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['400'],
    ),
    'body-s-compact-accent': makeFontSizeAndLineHeight(
      Typography['Body S']['font-size'],
      Typography['Body S']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['400'],
    ),
    'body-s-compact-regular': makeFontSizeAndLineHeight(
      Typography['Body S']['font-size'],
      Typography['Body S']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['400'],
    ),
    'body-xs-regular': makeFontSizeAndLineHeight(
      Typography['Body XS']['font-size'],
      Typography['Body XS']['line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
    ),
    'body-xs-accent': makeFontSizeAndLineHeight(
      Typography['Body XS']['font-size'],
      Typography['Body XS']['line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
    ),
    'body-xs-compact-accent': makeFontSizeAndLineHeight(
      Typography['Body XS']['font-size'],
      Typography['Body XS']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
    ),
    'body-xs-compact-regular': makeFontSizeAndLineHeight(
      Typography['Body XS']['font-size'],
      Typography['Body XS']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
    ),
    'caps-l': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['500-caps'],
      true,
    ),
    'caps-m': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['400-caps'],
      true,
    ),
    'caps-s': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['300-caps'],
      true,
    ),
    'body-tabular-l-accent': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['1100'],
    ),
    'body-tabular-l-regular': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['1100'],
    ),
    'body-tabular-l-compact-accent': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['1100'],
    ),
    'body-tabular-l-compact-regular': makeFontSizeAndLineHeight(
      Typography['Body L']['font-size'],
      Typography['Body L']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['1100'],
    ),
    'body-tabular-m-accent': makeFontSizeAndLineHeight(
      Typography['Body M']['font-size'],
      Typography['Body M']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['900'],
    ),
    'body-tabular-m-regular': makeFontSizeAndLineHeight(
      Typography['Body M']['font-size'],
      Typography['Body M']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['900'],
    ),
    'body-tabular-m-compact-accent': makeFontSizeAndLineHeight(
      Typography['Body M']['font-size'],
      Typography['Body M']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['900'],
    ),
    'body-tabular-m-compact-regular': makeFontSizeAndLineHeight(
      Typography['Body M']['font-size'],
      Typography['Body M']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['900'],
    ),
    'body-tabular-s-accent': makeFontSizeAndLineHeight(
      Typography['Body S']['font-size'],
      Typography['Body S']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['700'],
    ),
    'body-tabular-s-regular': makeFontSizeAndLineHeight(
      Typography['Body S']['font-size'],
      Typography['Body S']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['700'],
    ),
    'body-tabular-s-compact-accent': makeFontSizeAndLineHeight(
      Typography['Body S']['font-size'],
      Typography['Body S']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['700'],
    ),
    'body-tabular-s-compact-regular': makeFontSizeAndLineHeight(
      Typography['Body S']['font-size'],
      Typography['Body S']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['700'],
    ),
    'body-tabular-xs-accent': makeFontSizeAndLineHeight(
      Typography['Body XS']['font-size'],
      Typography['Body XS']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['500'],
    ),
    'body-tabular-xs-regular': makeFontSizeAndLineHeight(
      Typography['Body XS']['font-size'],
      Typography['Body XS']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['500'],
    ),
    'body-tabular-xs-compact-accent': makeFontSizeAndLineHeight(
      Typography['Body XS']['font-size'],
      Typography['Body XS']['compact-line-height'],
      Typography['font-weight'].semibold,
      Colors.content.primary,
      Typography['letter-spacing']['500'],
    ),
    'body-tabular-xs-compact-regular': makeFontSizeAndLineHeight(
      Typography['Body XS']['font-size'],
      Typography['Body XS']['compact-line-height'],
      Typography['font-weight'].regular,
      Colors.content.primary,
      Typography['letter-spacing']['500'],
    ),
  },
};

const palette = {
  colors: Colors,
  primary: {
    main: Colors.bg['action-primary'],
    dark: Colors['bg-active']['action-primary'],
    light: Colors.bg['action-secondary'],
    contrastText: Colors.core.white,
  },
  secondary: {
    main: Colors.bg['neutral-secondary'],
    dark: Colors['bg-active']['neutral-secondary'],
    light: Colors.bg['neutral-secondary'],
    contrastText: Colors.content.primary,
  },
  danger: {
    main: Colors.bg['danger-primary'],
    dark: Colors['bg-active']['danger-primary'],
    light: Colors.bg['danger-secondary'],
    contrastText: Colors.core.white,
  },
  tertiary: {
    main: Colors.special.nulled,
    dark: Colors.bg['action-secondary'],
    light: Colors.bg['danger-secondary'],
    contrastText: Colors.content['action-primary'],
  },
  'primary-inverted': {
    main: Colors.content['action-primary-inverted'],
    dark: Colors['bg-active']['action-primary'],
    light: Colors.bg['action-secondary'],
    contrastText: Colors.core.white,
  },
  action: {
    disabledBackground: Colors.bg['neutral-secondary'],
    disabled: Colors.content.tertiary,
  },
};

const typography = {
  fontFamily: Typography['font-family'],
  // Standard MUI typography variants (required)
  h1: tokens.typography['heading-l-accent'],
  h2: tokens.typography['heading-l-accent'],
  h3: tokens.typography['heading-m-accent'],
  h4: tokens.typography['heading-m-accent'],
  h5: tokens.typography['heading-s-accent'],
  h6: tokens.typography['heading-xs-accent'],
  subtitle1: tokens.typography['body-l-accent'],
  subtitle2: tokens.typography['body-m-accent'],
  body1: tokens.typography['body-m-regular'],
  body2: tokens.typography['body-s-regular'],
  button: tokens.typography['body-m-accent'],
  caption: tokens.typography['body-xs-regular'],
  overline: tokens.typography['caps-s'],
  // Additional custom MUI variants from common/all theme
  subtitleL: tokens.typography['body-l-accent'],
  subtitleM: tokens.typography['body-m-accent'],
  subtitleS: tokens.typography['body-s-accent'],
  subtitleXS: tokens.typography['body-xs-accent'],
  bodyL: tokens.typography['body-l-regular'],
  bodyM: tokens.typography['body-m-regular'],
  bodyS: tokens.typography['body-s-regular'],
  bodyXS: tokens.typography['body-xs-regular'],
  highlightGreen: tokens.typography['body-m-accent'],
  highlightRed: tokens.typography['body-m-accent'],
  ghostButtonTitle: tokens.typography['body-m-accent'],
  // Custom typography variants
  'heading-l-accent': tokens.typography['heading-l-accent'],
  'heading-m-accent': tokens.typography['heading-m-accent'],
  'heading-s-accent': tokens.typography['heading-s-accent'],
  'heading-xs-accent': tokens.typography['heading-xs-accent'],
  'body-l-regular': tokens.typography['body-l-regular'],
  'body-l-accent': tokens.typography['body-l-accent'],
  'body-l-compact-accent': tokens.typography['body-l-compact-accent'],
  'body-l-compact-regular': tokens.typography['body-l-compact-regular'],
  'body-m-regular': tokens.typography['body-m-regular'],
  'body-m-accent': tokens.typography['body-m-accent'],
  'body-m-compact-accent': tokens.typography['body-m-compact-accent'],
  'body-m-compact-regular': tokens.typography['body-m-compact-regular'],
  'body-s-regular': tokens.typography['body-s-regular'],
  'body-s-accent': tokens.typography['body-s-accent'],
  'body-s-compact-accent': tokens.typography['body-s-compact-accent'],
  'body-s-compact-regular': tokens.typography['body-s-compact-regular'],
  'body-xs-regular': tokens.typography['body-xs-regular'],
  'body-xs-accent': tokens.typography['body-xs-accent'],
  'body-xs-compact-accent': tokens.typography['body-xs-compact-accent'],
  'body-xs-compact-regular': tokens.typography['body-xs-compact-regular'],
  'caps-l': tokens.typography['caps-l'],
  'caps-m': tokens.typography['caps-m'],
  'caps-s': tokens.typography['caps-s'],
  'body-tabular-l-accent': tokens.typography['body-tabular-l-accent'],
  'body-tabular-l-regular': tokens.typography['body-tabular-l-regular'],
  'body-tabular-l-compact-accent': tokens.typography['body-tabular-l-compact-accent'],
  'body-tabular-l-compact-regular': tokens.typography['body-tabular-l-compact-regular'],
  'body-tabular-m-accent': tokens.typography['body-tabular-m-accent'],
  'body-tabular-m-regular': tokens.typography['body-tabular-m-regular'],
  'body-tabular-m-compact-accent': tokens.typography['body-tabular-m-compact-accent'],
  'body-tabular-m-compact-regular': tokens.typography['body-tabular-m-compact-regular'],
  'body-tabular-s-accent': tokens.typography['body-tabular-s-accent'],
  'body-tabular-s-regular': tokens.typography['body-tabular-s-regular'],
  'body-tabular-s-compact-accent': tokens.typography['body-tabular-s-compact-accent'],
  'body-tabular-s-compact-regular': tokens.typography['body-tabular-s-compact-regular'],
  'body-tabular-xs-accent': tokens.typography['body-tabular-xs-accent'],
  'body-tabular-xs-regular': tokens.typography['body-tabular-xs-regular'],
  'body-tabular-xs-compact-accent': tokens.typography['body-tabular-xs-compact-accent'],
  'body-tabular-xs-compact-regular': tokens.typography['body-tabular-xs-compact-regular'],
};

export const theme = createTheme({
  palette,
  typography,
  breakpoints: {
    values: {
      xs: 0, // Extra small devices (phones)
      sm: 744, // Small devices (tablets)
      md: 1140, // Medium devices (laptops)
      lg: 1728, // Large devices (desktops)
      xl: 1920, // Extra large devices (large desktops)
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        disableTouchRipple: true,
        variant: 'contained',
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
        icon: <span />,
        color: 'primary',
      },
    },
    MuiDialog: {
      defaultProps: {
        slotProps: {
          paper: {
            elevation: 0, // no shadow
          },
        },
      },
    },
    MuiDrawer: {
      defaultProps: {
        elevation: 0, // no shadow
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        notched: false,
      },
    },
    MuiChip: {
      defaultProps: {
        variant: 'filled',
        deleteIcon: <ClearIcon />,
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          'heading-l-accent': 'h1',
          'heading-m-accent': 'h1',
          'heading-s-accent': 'h1',
          'heading-xs-accent': 'h1',
          'body-l-regular': 'span',
          'body-l-accent': 'span',
          'body-l-compact-accent': 'span',
          'body-l-compact-regular': 'span',
          'body-m-regular': 'span',
          'body-m-accent': 'span',
          'body-m-compact-accent': 'span',
          'body-m-compact-regular': 'span',
          'body-s-regular': 'span',
          'body-s-accent': 'span',
          'body-s-compact-accent': 'span',
          'body-s-compact-regular': 'span',
          'body-xs-regular': 'span',
          'body-xs-accent': 'span',
          'body-xs-compact-accent': 'span',
          'body-xs-compact-regular': 'span',
          'caps-l': 'span',
          'caps-m': 'span',
          'caps-s': 'span',
          'body-tabular-l-accent': 'span',
          'body-tabular-l-regular': 'span',
          'body-tabular-l-compact-accent': 'span',
          'body-tabular-l-compact-regular': 'span',
          'body-tabular-m-accent': 'span',
          'body-tabular-m-regular': 'span',
          'body-tabular-m-compact-accent': 'span',
          'body-tabular-m-compact-regular': 'span',
          'body-tabular-s-accent': 'span',
          'body-tabular-s-regular': 'span',
          'body-tabular-s-compact-accent': 'span',
          'body-tabular-s-compact-regular': 'span',
          'body-tabular-xs-accent': 'span',
          'body-tabular-xs-regular': 'span',
          'body-tabular-xs-compact-accent': 'span',
          'body-tabular-xs-compact-regular': 'span',
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: false,
        variant: 'rounded',
      },
    },
    MuiStack: {
      defaultProps: {
        useFlexGap: true,
      },
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <CheckCircle />,
          alert: <AlertIcon />,
          warning: <AlertIcon />,
          info: <InfoCircle />,
          promotion: <Offer />,
        },
      },
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
    },
    MuiMenuItem: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
    },
    MuiListItemText: {
      defaultProps: {
        slotProps: {
          primary: { variant: 'body-m-regular' },
          secondary: { variant: 'body-s-regular' },
        },
      },
    },
    MuiFormControlLabel: {
      defaultProps: {
        slotProps: {
          typography: {
            variant: 'body-m-regular',
          },
        },
      },
    },
  },
});
